import { AuthHttp } from "../../AxiosConfig";

const ACCESS_TOKEN_STORAGE_KEY = "ADOPTION_A_T";
const REFRESH_TOKEN_STORAGE_KEY = "ADOPTION_R_T";
const AUTH_STATE_STORAGE_KEY = "ADOPTION_AUTH_STATE";

function storeAccessToken(token) {
  localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
}

function storeRefreshToken(token) {
  localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, token);
}

function clearStoredTokens() {
  clearStoredAccessToken();
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
}

function clearStoredAccessToken() {
  localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
}

function generateStateForAuth() {
  const randomStateForAuth = Date.now() + "-" + Math.random();
  localStorage.setItem(AUTH_STATE_STORAGE_KEY, randomStateForAuth);
  return randomStateForAuth;
}

function getSingleUseStoredAuthState() {
  const storedSingleUseState = localStorage.getItem(AUTH_STATE_STORAGE_KEY);
  // Remove this code immediately after fetching it to avoid duplicate request
  localStorage.removeItem(AUTH_STATE_STORAGE_KEY);
  return storedSingleUseState;
}

function getAccessTokens(code) {
  const requestParams = {
    code,
    client_id: process.env.REACT_APP_AUTH_CLIENT_ID,
    redirect_uri: getAuthRedirectUri(),
    grant_type: "authorization_code",
  };

  const data = Object.keys(requestParams)
    .map((key) => `${key}=${encodeURIComponent(requestParams[key])}`)
    .join("&");

  return AuthHttp.post("https://login.dts.utah.gov:443/sso/oauth2/access_token", data, {
    headers: {
      "content-type": "application/x-www-form-urlencoded"
    },
  }).then((response) => {
    if (response.data?.access_token) {
      storeAccessToken(response.data.access_token);
    }
    if (response.data?.refresh_token) {
      storeRefreshToken(response.data.refresh_token);
    }
    return response.data;
  });
}

function getAuthRedirectUri() {
  switch (process.env.REACT_APP_DOCKER_ENV) {
    case "LOCAL":
      return "http://localhost:3000/auth-redirect";
    case "DEV":
      return "https://adoption-dev.health.utah.gov/auth-redirect";
    case "AT":
      return "https://adoption-at.health.utah.gov/auth-redirect";
    case "PROD":
      return "https://adoption.health.utah.gov/auth-redirect";
    case "PROD_AWS":
      return "https://adoption.dhhs.utah.gov/auth-redirect";
    default: {
      console.error("Invalid environment value: " + process.env.REACT_APP_DOCKER_ENV);
    }
  }
}

function getStoredToken() {
  return localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
}

function getStoredRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
}

/**
 * If a refresh token is found, then a request will be made to refresh the user's access token
 * When successful, the token will be updated and the returned promise will resolve
 *
 * Promise is rejected on error.
 * @returns a promise
 */
function attemptTokenRefresh() {
  clearStoredAccessToken();
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    return Promise.reject(new Error("No refresh token found"));
  }

  return new Promise((resolve, reject) => {
    const requestParams = {
      client_id: process.env.REACT_APP_AUTH_CLIENT_ID,
      grant_type: "refresh_token",
      refresh_token: refreshToken,
    };

    const data = Object.keys(requestParams)
      .map((key) => `${key}=${encodeURIComponent(requestParams[key])}`)
      .join("&");

    return AuthHttp.post("https://login.dts.utah.gov:443/sso/oauth2/access_token", data, {
      headers: {
        "content-type": "application/x-www-form-urlencoded",
      },
    })
      .then((response) => {
        if (response.data?.access_token) {
          storeAccessToken(response.data.access_token);
          resolve();
        } else {
          reject(new Error("access token not returned"));
        }
      })
      .catch(reject);
  });
}

const TokenService = {
  getAccessTokens,
  getAuthRedirectUri,
  getStoredToken,
  clearStoredTokens,
  generateStateForAuth,
  getSingleUseStoredAuthState,
  attemptTokenRefresh,
};

export default TokenService;
